import { useState } from 'react';
import { Copy, Check, Search } from 'lucide-react';

interface TranscriptViewerProps {
  agentTranscript?: string;
  customerTranscript?: string;
  fullTranscript?: string;
  onTimestampClick?: (seconds: number) => void;
}

export function TranscriptViewer({
  agentTranscript,
  customerTranscript,
  fullTranscript,
  onTimestampClick
}: TranscriptViewerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [copied, setCopied] = useState(false);
  const [viewMode, setViewMode] = useState<'combined' | 'agent' | 'customer'>('combined');

  const copyToClipboard = () => {
    const text = fullTranscript || agentTranscript || customerTranscript || '';
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const highlightSearchTerm = (text: string) => {
    if (!searchTerm) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    return text.split(regex).map((part, i) => 
      regex.test(part) 
        ? <mark key={i} className="bg-yellow-300 text-gray-900">{part}</mark>
        : part
    );
  };

  const renderTranscript = () => {
    if (!fullTranscript && !agentTranscript && !customerTranscript) {
      return (
        <div className="text-center py-8 text-slate-400">
          <p>No transcript available</p>
          <p className="text-xs mt-2">Transcription may still be processing</p>
        </div>
      );
    }

    // For now, show combined transcript
    // In production, you'd parse this to show speaker-separated lines
    const transcript = fullTranscript || '';
    const lines = transcript.split('\n').filter(Boolean);

    if (lines.length === 0) {
      return <div className="text-slate-400 text-sm">Transcript is empty</div>;
    }

    return (
      <div className="space-y-2">
        {lines.map((line, index) => {
          const isAgent = line.toLowerCase().startsWith('agent:') || line.toLowerCase().startsWith('a:');
          const isCustomer = line.toLowerCase().startsWith('customer:') || line.toLowerCase().startsWith('c:');
          
          return (
            <div
              key={index}
              className={`p-3 rounded-lg ${
                isAgent ? 'bg-blue-900/30 border-l-4 border-blue-500' :
                isCustomer ? 'bg-green-900/30 border-l-4 border-green-500' :
                'bg-slate-800'
              }`}
            >
              <div className="text-sm text-slate-200">
                {highlightSearchTerm(line)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bg-slate-900 rounded-lg p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-200">Call Transcript</h3>
        <div className="flex items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search transcript..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-3 py-1.5 text-xs bg-slate-800 border border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-200 placeholder-slate-500"
            />
          </div>

          {/* Copy Button */}
          <button
            onClick={copyToClipboard}
            className="p-2 rounded-lg hover:bg-slate-800 text-slate-300 transition-colors"
            title="Copy transcript"
          >
            {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="flex gap-2">
        {['combined', 'agent', 'customer'].map((mode) => (
          <button
            key={mode}
            onClick={() => setViewMode(mode as any)}
            className={`px-3 py-1.5 text-xs rounded-lg capitalize transition-colors ${
              viewMode === mode
                ? 'bg-blue-600 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            {mode}
          </button>
        ))}
      </div>

      {/* Transcript Content */}
      <div className="max-h-96 overflow-y-auto">
        {viewMode === 'combined' && renderTranscript()}
        {viewMode === 'agent' && agentTranscript && (
          <div className="bg-blue-900/30 border-l-4 border-blue-500 p-4 rounded-lg">
            <div className="text-sm text-slate-200 whitespace-pre-wrap">
              {highlightSearchTerm(agentTranscript)}
            </div>
          </div>
        )}
        {viewMode === 'customer' && customerTranscript && (
          <div className="bg-green-900/30 border-l-4 border-green-500 p-4 rounded-lg">
            <div className="text-sm text-slate-200 whitespace-pre-wrap">
              {highlightSearchTerm(customerTranscript)}
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex gap-4 text-xs text-slate-400 pt-2 border-t border-slate-800">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-blue-900/30 border-l-4 border-blue-500"></div>
          <span>Agent</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-900/30 border-l-4 border-green-500"></div>
          <span>Customer</span>
        </div>
      </div>
    </div>
  );
}
